import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import ClipLoader from 'react-spinners/ClipLoader';
import styles from './List.module.scss';
import {
    addComparisonSelectedItem,
    removeComparisonSelectedItem,
} from '../../../Comparison/comparisonActions';
import * as listActions from '../../listActions';
import { RootState } from 'MyTypes';
import { comparisonSelectedUuidsSelector } from '../../../Comparison/comparisonSelectors';
import { MappedListItem } from '../../listTypes';

const mapStateToProps = (state: RootState) => ({
    selectedUuids: comparisonSelectedUuidsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            addComparisonSelectedItem: addComparisonSelectedItem,
            removeComparisonSelectedItem: removeComparisonSelectedItem,
            getPagePath: listActions.getListItemPage,
        },
        dispatch,
    );

interface ListItemProps {
    listItem: MappedListItem;
}

type Props = ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    ListItemProps;

const ListItem: React.FC<Props> = props => {
    const { selectedUuids, listItem } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isComparedLocalState, setIsComparedLocalState] = useState(
        selectedUuids.includes(listItem.uuid), // todo this will need to change if selected structure changes?
    );

    useEffect(() => {
        setIsComparedLocalState(selectedUuids.includes(listItem.uuid));
    }, [selectedUuids, setIsComparedLocalState, listItem.uuid]);

    useEffect(() => {
        if (
            listItem._pagePath ||
            listItem.isFetched ||
            listItem.isListItemError
        ) {
            setLoading(false);
        }
    }, [
        listItem._pagePath,
        listItem.isFetched,
        listItem.isListItemError,
        setLoading,
    ]);

    const handleLink = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        if (listItem._pagePath) window.open(listItem._pagePath);
        event.stopPropagation();
    };

    const handleCompare = (
        event: React.MouseEvent<HTMLLabelElement, MouseEvent>,
    ) => {
        const updatedIsCompared = !isComparedLocalState;

        if (updatedIsCompared && selectedUuids.length >= 5) {
            return;
        }

        if (updatedIsCompared) {
            props.addComparisonSelectedItem(listItem.uuid, listItem.header);
        } else {
            props.removeComparisonSelectedItem(listItem.uuid);
        }

        setIsComparedLocalState(updatedIsCompared);
        event.stopPropagation();
    };

    const handleOpen = () => {
        if (
            !listItem._pagePath &&
            !listItem.isFetched &&
            !listItem.isListItemError
        ) {
            setLoading(true);
            props.getPagePath(listItem.uuid);
        }
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };
    return isOpen && !isLoading ? (
        <div
            id={'list-list-item-container'}
            className={styles.listItemContainer}
            onClick={handleClose}
        >
            <div
                id={'list-list-item-toggle-open'}
                className={styles.listItemToggleOpen}
            >
                -
            </div>
            <div className={styles.listItemContentOpen}>
                <div
                    id={'list-list-item-header-open'}
                    className={styles.listItemHeaderOpen}
                >
                    {listItem.header}
                </div>
                <div className={styles.listItemContentExpanded}>
                    <div className={styles.listItemDescription}>
                        {listItem.description}
                    </div>
                    <div className={styles.listItemCompareCTAContainer}>
                        <label
                            htmlFor={listItem.uuid}
                            className={styles.listItemCompare}
                            onClick={event => handleCompare(event)}
                        >
                            Add to compare
                            <input
                                className={styles.listItemCompareBox}
                                id={listItem.uuid}
                                type={'checkbox'}
                                checked={isComparedLocalState}
                                disabled={
                                    !isComparedLocalState &&
                                    selectedUuids.length >= 5
                                }
                                readOnly
                            />
                        </label>
                    </div>
                    {listItem._pagePath && (
                        <div className={styles.listItemReadMoreContainer}>
                            <button
                                id={'list-list-item-button'}
                                className={styles.listItemButton}
                                onClick={event => handleLink(event)}
                            >
                                Read More
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    ) : (
        <div
            id={'list-list-item-container'}
            className={styles.listItemContainer}
            onClick={handleOpen}
        >
            <div className={styles.listItemToggle}>+</div>
            <div className={styles.listItemContent}>
                <div className={styles.listItemHeader}>{listItem.header}</div>
                <ClipLoader size={26} color={'#aaabad'} loading={isLoading} />
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ListItem);
